import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import { FullImageSection, ColorTitle } from '@/components/Layouts'
import { getImageData } from '@/utilites/getImageData'
import { resolveHref } from '@/utilites/resolveHref'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Chip from '@mui/material/Chip';
import VerifiedIcon from '@mui/icons-material/Verified';

interface ButtonData {
    text?: string
    link?: string
}

interface Highlight {
    title?: string
    subtitle?: string
}

interface Content {
    title?: string
    subtitle?: string
    badge?: string
    image?: string
    highlights?: Highlight[]
    button?: ButtonData
    button2?: ButtonData
}

interface S1_Hero_1Props {
    content?: Content
    blockName?:string
}

const Index = (props: S1_Hero_1Props) => {
    const { content = {} } = props
    const { title, subtitle, button = {}, button2 = {}, image, badge, highlights } = content
    const heroImage = getImageData(image)
    const primaryHref = resolveHref(button?.link, '#')
    const secondaryHref = resolveHref(button2?.link, '#')
    // Extract image ID if available (for cache busting)
    const imageId = typeof image === 'object' && image !== null && 'id' in image 
        ? (image as any).id 
        : typeof image === 'string' 
            ? image 
            : null
    
    // Use image ID and URL as key to force re-render when image changes
    const imageKey = imageId ? `${imageId}-${heroImage?.src}` : heroImage?.src || JSON.stringify(image)

    return (
        <FullImageSection
            key={imageKey}
            aria-label='S1_Hero_1'
            image={heroImage}
            
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    textAlign: 'center',
                    gap: 3,
                    width: {
                        xs: '80vw',
                        md: '80vw',
                        lg: '60vw',
                        xl: '50vw',
                    },
                }}
            >
                <Chip icon={<VerifiedIcon />} label={badge || ' Licensed & Insured • 15+ Years Experience'} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)', fontWeight: 'bold', color: 'lightgray' }} />


                <ColorTitle
                    title={title}
                    variant='h1'
                    colorFrom={1}
                    colorTo={3}
                    textAlign='center'
                    color='white'
                    width='100%'
                />

                <Typography
                    variant='h5'
                    color='white'
                    sx={{ textAlign: 'center' }}
                >
                    {subtitle}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', md: 'row' },
                    }}
                >
                    {button?.text && (
                        <Button
                            href={primaryHref}
                            variant='contained'
                            color='primary'
                            size='large'
                            endIcon={<ArrowForwardIcon />}
                        >
                            {button.text}
                        </Button>
                    )}
                    {button2?.text && (
                        <Button
                            variant='outlined'
                            color='secondary'
                            size='large'
                            href={secondaryHref}
                        >
                            {button2.text}
                        </Button>
                    )}
                </Box>
                <Grid
                    container
                    spacing={4}
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'row',
                        width: {
                            xs: '80vw',
                            md: '80vw',
                            lg: '60vw',
                            xl: '50vw',
                        },
                        margin: '0 auto',
                        mt: 8,
                        textAlign: 'center',
                    }}
                >
                    {highlights?.map((highlight, index) => (
                        <Grid size={{ xs: 12, md: 4 }} key={'highlight-' + index}>
                            <Typography
                                component='h3'
                                variant='h2'
                                sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}
                            >
                                {highlight?.title}
                            </Typography>
                            <Typography
                                variant='body1'
                                sx={{ color: 'grey.300' }}
                            >
                                {highlight?.subtitle}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </FullImageSection>
    )
}

export default Index

